import React from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import {
  getGuestDirectorySectionBySlug,
  getGuestDirectoryContentsBySectionSlug,
  getGuestDirectoryIsLoading,
  getGuestDirectoryError,
  getGuestDirectoryRowColour,
  getGuestDirectoryAlternateRowColour,
  getGuestDirectoryLinkButtonColour,
  getGuestDirectoryLinkButtonBg,
  getGuestDirectoryLinkButtonBorder,
} from 'selectors/guestDirectory';
import Loading from 'components/Loading';
import MessageBlock from 'components/MessageBlock';
import { getLocale } from 'selectors/user';
import GuestDirectorySectionInfo from './SectionInfo';
import GuestDirectoryContentRow from './ContentRow';
import { BannerImage, BannerImageWrapper } from './styles';

interface RouteParams {
  menuId: string;
  sectionId: string;
}

interface Props {
  params: RouteParams;
}

const GuestDirectorySection: React.FC<Props> = ({ params }) => {
  const menuSlug = params.menuId;
  const sectionSlug = params.sectionId;
  const isLoading = useSelector(getGuestDirectoryIsLoading);
  const error = useSelector(getGuestDirectoryError);
  const currentSection = useSelector(state => getGuestDirectorySectionBySlug(state, menuSlug, sectionSlug));
  const contents = useSelector(state => getGuestDirectoryContentsBySectionSlug(state, menuSlug, sectionSlug));
  const currentLocale = useSelector(getLocale);
  const rowColour = useSelector(getGuestDirectoryRowColour);
  const alternateRowColour = useSelector(getGuestDirectoryAlternateRowColour);
  const linkButtonColour = useSelector(getGuestDirectoryLinkButtonColour);
  const linkButtonBg = useSelector(getGuestDirectoryLinkButtonBg);
  const linkButtonBorder = useSelector(getGuestDirectoryLinkButtonBorder);

  if (isLoading) {
    return (
      <Loading>
        <FormattedMessage defaultMessage="Loading section..." />
      </Loading>
    );
  }

  if (error) {
    return (
      <MessageBlock
        header={<FormattedMessage defaultMessage="Sorry" />}
        body={<FormattedMessage defaultMessage="Unable to load this section. Please try again later." />}
      />
    );
  }

  if (!currentSection) {
    return (
      <MessageBlock
        header={<FormattedMessage defaultMessage="Sorry" />}
        body={<FormattedMessage defaultMessage="Section not found." />}
      />
    );
  }

  const contactInfo =
    currentSection.translations?.[currentLocale]?.contact_info || currentSection.contact_info;
  const address = currentSection.translations?.[currentLocale]?.address || currentSection.address;
  const checkInTimeInfo =
    currentSection.translations?.[currentLocale]?.check_in_time_info || currentSection.check_in_time_info;
  const description1 =
    currentSection.translations?.[currentLocale]?.description_1 ?? currentSection.description_1 ?? null;
  const description2 =
    currentSection.translations?.[currentLocale]?.description_2 ?? currentSection.description_2 ?? null;
  const description3 =
    currentSection.translations?.[currentLocale]?.description_3 ?? currentSection.description_3 ?? null;

  return (
    <div>
      {currentSection.images?.[0] && (
        <BannerImageWrapper>
          <BannerImage src={currentSection.images[0].path} alt={currentSection.images[0].alt_text || ''} />
        </BannerImageWrapper>
      )}
      <GuestDirectorySectionInfo
        contactInfo={contactInfo}
        address={address}
        checkInTimeInfo={checkInTimeInfo}
        description1={description1}
        description2={description2}
        description3={description3}
      />
      {contents.length === 0 ? (
        <MessageBlock
          header={<FormattedMessage defaultMessage="Sorry" />}
          body={<FormattedMessage defaultMessage="This section has no content available." />}
        />
      ) : (
        contents.map((content, index) => (
          <GuestDirectoryContentRow
            key={content.id}
            content={content}
            locale={currentLocale}
            rowColour={rowColour}
            alternateRowColour={alternateRowColour}
            isEven={index % 2 === 1}
            linkButtonColour={linkButtonColour}
            linkButtonBg={linkButtonBg}
            linkButtonBorder={linkButtonBorder}
          />
        ))
      )}
    </div>
  );
};

export default GuestDirectorySection;
